import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { CloudUpload as UploadIcon, Clear as ClearIcon } from '@mui/icons-material';
import type { DashboardBannerStrip, DashboardHeaderChrome, DashboardHeaderTitleSize } from './types';
import { SettingsColorPicker, SettingsSelect } from './controls';
import { prepareDashboardLogo } from './dashboardLogoUtils';

const TITLE_SIZE_OPTIONS = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
];

const LOGO_POSITION_OPTIONS = [
  { value: 'left', label: 'Left of title' },
  { value: 'right', label: 'Right (near filters)' },
];

const DashboardHeaderSettingsDialog: React.FC<{
  open: boolean;
  initialChrome: DashboardHeaderChrome;
  initialBanner?: DashboardBannerStrip;
  onClose: () => void;
  onSave: (chrome: DashboardHeaderChrome, banner: DashboardBannerStrip | undefined) => void;
}> = ({ open, initialChrome, initialBanner, onClose, onSave }) => {
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [logoLoading, setLogoLoading] = useState(false);
  const [logoError, setLogoError] = useState('');
  const [logoAlign, setLogoAlign] = useState<'left' | 'right'>('left');
  const [backgroundColor, setBackgroundColor] = useState('');
  const [titleColor, setTitleColor] = useState('');
  const [subtitleColor, setSubtitleColor] = useState('');
  const [titleFontSize, setTitleFontSize] = useState<DashboardHeaderTitleSize>('medium');
  const [showLastUpdated, setShowLastUpdated] = useState(true);
  const [bannerOn, setBannerOn] = useState(false);
  const [bannerText, setBannerText] = useState('');
  const [bannerBg, setBannerBg] = useState('');
  const [bannerFg, setBannerFg] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setTitle(initialChrome.title ?? '');
    setSubtitle(initialChrome.subtitle ?? '');
    setLogoUrl(initialChrome.logoUrl ?? '');
    setLogoLoading(false);
    setLogoError('');
    setLogoAlign(initialChrome.logoAlign ?? 'left');
    setBackgroundColor(initialChrome.backgroundColor ?? '');
    setTitleColor(initialChrome.titleColor ?? '');
    setSubtitleColor(initialChrome.subtitleColor ?? '');
    setTitleFontSize(initialChrome.titleFontSize ?? 'medium');
    setShowLastUpdated(initialChrome.showLastUpdated !== false);
    const b = initialBanner?.text?.trim();
    setBannerOn(Boolean(b));
    setBannerText(initialBanner?.text ?? '');
    setBannerBg(initialBanner?.backgroundColor ?? '');
    setBannerFg(initialBanner?.textColor ?? '');
    // Capture the latest saved values when the dialog opens. Depending on the
    // object props here would reset an upload whenever the parent re-rendered.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleLogoUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setLogoLoading(true);
    setLogoError('');
    try {
      const preparedLogo = await prepareDashboardLogo(file);
      setLogoUrl(preparedLogo);
    } catch (error) {
      setLogoError(error instanceof Error ? error.message : 'The logo could not be loaded.');
    } finally {
      setLogoLoading(false);
    }
  }, []);

  const handleSave = () => {
    const chrome: DashboardHeaderChrome = {
      title: title.trim() || 'Dashboard',
      subtitle: subtitle.trim() || undefined,
      logoUrl: logoUrl.trim() || undefined,
      logoAlign,
      backgroundColor: backgroundColor.trim() || undefined,
      titleColor: titleColor.trim() || undefined,
      subtitleColor: subtitleColor.trim() || undefined,
      titleFontSize,
      showLastUpdated,
    };
    let banner: DashboardBannerStrip | undefined;
    if (bannerOn && bannerText.trim()) {
      banner = {
        text: bannerText.trim(),
        backgroundColor: bannerBg.trim() || undefined,
        textColor: bannerFg.trim() || undefined,
      };
    }
    onSave(chrome, banner);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Header appearance</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Full-width bar above the widget grid. Filter widgets in the header apply filters to all widgets in the
            list tiles.
          </Typography>
          <TextField
            label="Title"
            name="dashboardHeaderTitle"
            fullWidth
            autoFocus
            value={title}
            onChange={(event) => setTitle(event.currentTarget.value)}
            inputProps={{ readOnly: false }}
          />
          <TextField
            label="Subtitle"
            fullWidth
            multiline
            minRows={2}
            value={subtitle}
            onChange={(e) => setSubtitle(e.target.value)}
          />
          <Typography variant="caption" color="text.secondary">Logo image</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              label="Logo image URL"
              fullWidth
              size="small"
              value={logoUrl.startsWith('data:') ? '(uploaded image)' : logoUrl}
              onChange={(e) => {
                setLogoUrl(e.target.value);
                setLogoError('');
              }}
              placeholder="https://… or upload an image"
              InputProps={{
                readOnly: logoUrl.startsWith('data:'),
              }}
            />
            <Tooltip title="Upload image">
              <IconButton
                onClick={() => fileInputRef.current?.click()}
                size="small"
                disabled={logoLoading}
                sx={{ flexShrink: 0 }}
              >
                {logoLoading ? <CircularProgress size={20} /> : <UploadIcon />}
              </IconButton>
            </Tooltip>
            {logoUrl && (
              <Tooltip title="Clear logo">
                <IconButton onClick={() => { setLogoUrl(''); setLogoError(''); }} size="small" sx={{ flexShrink: 0 }}>
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
              style={{ display: 'none' }}
              onChange={handleLogoUpload}
            />
          </Stack>
          {logoError && <Alert severity="error">{logoError}</Alert>}
          {logoUrl && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.5 }}>
              <Box
                component="img"
                src={logoUrl}
                alt="Logo preview"
                onLoad={() => setLogoError('')}
                onError={() => setLogoError('The logo preview could not be displayed.')}
                sx={{ maxHeight: 48, maxWidth: '100%', objectFit: 'contain', borderRadius: 0.5 }}
              />
            </Box>
          )}
          <SettingsSelect
            label="Logo position"
            value={logoAlign}
            onChange={(v) => setLogoAlign(v as 'left' | 'right')}
            options={LOGO_POSITION_OPTIONS}
          />
          <SettingsColorPicker
            label="Header background"
            value={backgroundColor}
            onChange={setBackgroundColor}
            placeholder="#1e1e1e or rgba(…)"
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <SettingsColorPicker
              label="Title color"
              value={titleColor}
              onChange={setTitleColor}
            />
            <SettingsColorPicker
              label="Subtitle color"
              value={subtitleColor}
              onChange={setSubtitleColor}
            />
          </Stack>
          <SettingsSelect
            label="Title size"
            value={titleFontSize}
            onChange={(v) => setTitleFontSize(v as DashboardHeaderTitleSize)}
            options={TITLE_SIZE_OPTIONS}
          />
          <FormControlLabel
            control={<Switch checked={showLastUpdated} onChange={(e) => setShowLastUpdated(e.target.checked)} />}
            label="Show last updated time"
          />

          <Typography variant="subtitle2" sx={{ pt: 1 }}>
            Optional banner (below header)
          </Typography>
          <FormControlLabel
            control={<Switch checked={bannerOn} onChange={(_, checked) => setBannerOn(checked)} />}
            label="Enable banner"
          />
          <TextField
            label="Banner text"
            fullWidth
            disabled={!bannerOn}
            value={bannerText}
            onChange={(e) => setBannerText(e.target.value)}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <SettingsColorPicker
              label="Banner background"
              value={bannerBg}
              onChange={setBannerBg}
              disabled={!bannerOn}
            />
            <SettingsColorPicker
              label="Banner text color"
              value={bannerFg}
              onChange={setBannerFg}
              disabled={!bannerOn}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={logoLoading || Boolean(logoError)}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DashboardHeaderSettingsDialog;
